// frontend/src/pages/Reports/ReportsHub.js
import React, { useState } from 'react';
import { Card, Row, Col, Typography, Button, Tag, Spin, Alert, Space, Divider } from 'antd';
import { 
  BarChartOutlined, 
  TeamOutlined, 
  UserOutlined,
  FileTextOutlined,
  EyeOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  FileExcelOutlined,
  FormOutlined,
  FileWordOutlined,
  FilePdfOutlined,
  HistoryOutlined,
  FieldTimeOutlined,
  CalendarOutlined  // NEW: For Man-Day Report
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useNotificationApi } from '../../context/NotificationContext';
import './ReportsHub.css';

const { Title, Text } = Typography;

const ReportsHub = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const notificationApi = useNotificationApi();
  const [loading, setLoading] = useState(false);

  // Interactive reports (Create & Submit - with workflow)
  const interactiveReports = [
    {
      id: 'summary-service-report',
      title: 'Summary Service Report',
      description: 'Monthly contract compliance report. Create, fill daily activities, and submit for signature workflow (Internal Standard → Internal Supervisor → Customer Supervisor).',
      icon: <FormOutlined className="report-icon-svg" />,
      path: '/reports/summary-service-report',
      tags: ['Service Report', 'Monthly', 'Signatures', 'Compliance', 'Workflow'],
      status: 'active',
      color: '#722ed1',
      bgColor: '#f9f0ff'
    }
  ];

  // ============================================================
  // NEW: Man-Day Report added to timeTrackingReports
  // ============================================================
  const timeTrackingReports = [
    {
      id: 'manday-report',
      title: 'Man-Days Service Report',
      description: 'Generate monthly Man-Days Service Report based on contract and time entries. Shows CM, CR, PM, and Others with total hours and man days. Output in DOCX or PDF format.',
      icon: <CalendarOutlined className="report-icon-svg" />,
      path: '/reports/manday',
      tags: ['Man-Days', 'Monthly', 'Contract', 'CM/CR', 'Hours', 'PDF/DOCX'],
      status: 'active',
      color: '#13c2c2',
      bgColor: '#e6fffb'
    },
    {
      id: 'cr-single-report',
      title: 'CR/CM Activity Report',
      description: 'Generate detailed report for a single Change Request (CR) or Corrective Maintenance (CM). Includes all activity entries, total hours, man days, and breakdown by activity type.',
      icon: <FieldTimeOutlined className="report-icon-svg" />,
      path: '/time-tracking/reports/single',
      tags: ['CR/CM', 'Activity', 'Hours', 'Man Days', 'Detailed'],
      status: 'active',
      color: '#13c2c2',
      bgColor: '#e6fffb'
    },
    {
      id: 'cr-monthly-report',
      title: 'Monthly CR/CM Consolidated Report',
      description: 'Generate monthly consolidated report for all CR/CM activities under a contract. Shows total hours, man days, and breakdown per CR/CM with grand totals.',
      icon: <HistoryOutlined className="report-icon-svg" />,
      path: '/time-tracking/reports/monthly',
      tags: ['Monthly', 'Consolidated', 'CR/CM', 'Hours', 'Man Days'],
      status: 'active',
      color: '#fa8c16',
      bgColor: '#fff7e6'
    }
  ];

  // Analytics reports (View only)
  const analyticsReports = [
    {
      id: 'contract-ticket-volume',
      title: 'Contract Ticket Volume',
      description: 'Monthly ticket volume breakdown by contract. Track trends and identify workload distribution across contracts over time.',
      icon: <BarChartOutlined className="report-icon-svg" />,
      path: '/reports/contract-ticket-volume',
      tags: ['Contract', 'Volume', 'Monthly'],
      status: 'active',
      color: '#1890ff',
      bgColor: '#e6f7ff'
    },
    {
      id: 'summary-borang-aduan',
      title: 'Summary Borang Aduan',
      description: 'Generate summary complaint reports for closed tickets. Select single month or date range to view tickets from your organization.',
      icon: <FileExcelOutlined className="report-icon-svg" />,
      path: '/reports/summary-borang-aduan',
      tags: ['Borang Aduan', 'Closed Tickets', 'Monthly', 'Date Range'],
      status: 'active',
      color: '#52c41a',
      bgColor: '#f6ffed'
    }
  ];

  // Coming soon reports (future features)
  const comingSoonReports = [
    {
      id: 'tickets-by-organization',
      title: 'Tickets by Organization',
      description: 'Analyze ticket distribution across different organizations. Compare performance metrics and workload allocation.',
      icon: <TeamOutlined className="report-icon-svg" />,
      path: '/reports/tickets-by-organization',
      tags: ['Organization', 'Distribution', 'Analytics'],
      status: 'coming-soon',
      color: '#faad14',
      bgColor: '#fffbe6'
    },
    {
      id: 'tickets-by-user',
      title: 'Tickets by User',
      description: 'Track ticket creation and assignment by individual users. Monitor user workload, response times, and performance.',
      icon: <UserOutlined className="report-icon-svg" />,
      path: '/reports/tickets-by-user',
      tags: ['User', 'Performance', 'Workload'],
      status: 'coming-soon',
      color: '#722ed1',
      bgColor: '#f9f0ff'
    },
    {
      id: 'contract-summary',
      title: 'Contract Performance Summary',
      description: 'Overview of ticket metrics by contract including closure rates, average resolution times, and SLA compliance.',
      icon: <FileTextOutlined className="report-icon-svg" />,
      path: '/reports/contract-summary',
      tags: ['Contract', 'SLA', 'Performance'],
      status: 'coming-soon',
      color: '#eb2f96',
      bgColor: '#fff0f6'
    }
  ];

  const showError = (title, content, key = 'reportError') => {
    notificationApi.error({
      key,
      message: title,
      description: content,
      duration: 4.5,
      placement: 'topRight',
    });
  };

  const handleViewReport = (report) => {
    if (report.status === 'active') {
      navigate(report.path);
    } else {
      showError('Coming Soon', `${report.title} will be available in a future update.`);
    }
  };

  if (loading) {
    return (
      <div className="reports-hub-loading">
        <Spin size="large" tip="Loading reports..." />
      </div>
    );
  }

  return (
    <div className="reports-hub">
      <div className="reports-hub-header">
        <Title level={2} className="reports-hub-title">Analytics & Reports</Title>
        <Text className="reports-hub-description">
          Generate and view comprehensive reports to analyze ticket data, track performance, and gain actionable insights.
        </Text>
      </div>

      {/* Interactive Reports Section (with workflow) */}
      {interactiveReports.length > 0 && (
        <>
          <div className="reports-section">
            <div style={{ marginBottom: 16 }}>
              <Tag icon={<FormOutlined />} color="purple" style={{ padding: '4px 12px', fontSize: '14px' }}>
                Interactive Reports (Create & Submit)
              </Tag>
              <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
                These reports require data entry and follow a signature workflow
              </Text>
            </div>
            <Row gutter={[24, 24]}>
              {interactiveReports.map((report) => (
                <Col xs={24} sm={24} md={12} lg={12} xl={12} key={report.id}>
                  <Card 
                    className="report-card"
                    hoverable
                    onClick={() => handleViewReport(report)}
                    style={{ 
                      borderTop: `4px solid ${report.color}`,
                      cursor: 'pointer'
                    }}
                  >
                    <div className="report-card-content">
                      <div className="report-card-icon" style={{ backgroundColor: report.bgColor, color: report.color }}>
                        {report.icon}
                      </div>
                      <div className="report-card-info">
                        <div className="report-card-header">
                          <Title level={4} className="report-card-title">{report.title}</Title>
                          <Tag color="purple" className="report-card-tag">Interactive</Tag>
                        </div>
                        <Text type="secondary" className="report-card-description">
                          {report.description}
                        </Text>
                        <div className="report-card-tags">
                          {report.tags.map((tag) => (
                            <Tag key={tag} color="purple" className="report-tag">
                              {tag}
                            </Tag>
                          ))}
                        </div>
                        <div className="report-card-footer">
                          <Button 
                            type="primary" 
                            icon={<FormOutlined />}
                            size="middle"
                            className="report-card-button"
                            style={{ background: report.color, borderColor: report.color }}
                          >
                            Create / Manage
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
          <Divider className="reports-divider" />
        </>
      )}

      {/* CR/CM Time Tracking Reports Section */}
      <div className="reports-section">
        <div style={{ marginBottom: 16 }}>
          <Tag icon={<FieldTimeOutlined />} color="cyan" style={{ padding: '4px 12px', fontSize: '14px' }}>
            CR/CM Time Tracking Reports
          </Tag>
          <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
            Generate time tracking reports for Change Requests (CR) and Corrective Maintenance (CM)
          </Text>
        </div>
        <Row gutter={[24, 24]}>
          {timeTrackingReports.map((report) => (
            <Col xs={24} sm={24} md={12} lg={12} xl={12} key={report.id}>
              <Card 
                className="report-card"
                hoverable
                onClick={() => handleViewReport(report)}
                style={{ 
                  borderTop: `4px solid ${report.color}`,
                  cursor: 'pointer'
                }}
              >
                <div className="report-card-content">
                  <div className="report-card-icon" style={{ backgroundColor: report.bgColor, color: report.color }}>
                    {report.icon}
                  </div>
                  <div className="report-card-info">
                    <div className="report-card-header">
                      <Title level={4} className="report-card-title">{report.title}</Title>
                      <Tag color="cyan" className="report-card-tag">Available</Tag>
                    </div>
                    <Text type="secondary" className="report-card-description">
                      {report.description}
                    </Text>
                    <div className="report-card-tags">
                      {report.tags.map((tag) => (
                        <Tag key={tag} color="cyan" className="report-tag">
                          {tag}
                        </Tag>
                      ))}
                    </div>
                    <div className="report-card-footer">
                      <Button 
                        type="primary" 
                        icon={<EyeOutlined />}
                        size="middle"
                        className="report-card-button"
                        style={{ background: report.color, borderColor: report.color }}
                      >
                        Generate Report
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      </div>

      {/* Divider between time tracking and analytics reports */}
      <Divider className="reports-divider" />

      {/* Analytics Reports Section (View Only) */}
      <div className="reports-section">
        <div style={{ marginBottom: 16 }}>
          <Tag icon={<BarChartOutlined />} color="blue" style={{ padding: '4px 12px', fontSize: '14px' }}>
            Analytics Reports (View Only)
          </Tag>
          <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
            Generate on-demand reports with filters and export options
          </Text>
        </div>
        <Row gutter={[24, 24]}>
          {analyticsReports.map((report) => (
            <Col xs={24} sm={24} md={12} lg={12} xl={12} key={report.id}>
              <Card 
                className="report-card"
                hoverable
                onClick={() => handleViewReport(report)}
                style={{ 
                  borderTop: `4px solid ${report.color}`,
                  cursor: 'pointer'
                }}
              >
                <div className="report-card-content">
                  <div className="report-card-icon" style={{ backgroundColor: report.bgColor, color: report.color }}>
                    {report.icon}
                  </div>
                  <div className="report-card-info">
                    <div className="report-card-header">
                      <Title level={4} className="report-card-title">{report.title}</Title>
                      <Tag color="green" className="report-card-tag">Available</Tag>
                    </div>
                    <Text type="secondary" className="report-card-description">
                      {report.description}
                    </Text>
                    <div className="report-card-tags">
                      {report.tags.map((tag) => (
                        <Tag key={tag} color="blue" className="report-tag">
                          {tag}
                        </Tag>
                      ))}
                    </div>
                    <div className="report-card-footer">
                      <Button 
                        type="primary" 
                        icon={<EyeOutlined />}
                        size="middle"
                        className="report-card-button"
                      >
                        View Report
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      </div>

      {/* Divider between analytics and coming soon reports */}
      {comingSoonReports.length > 0 && (
        <>
          <Divider className="reports-divider">
            <Tag icon={<ClockCircleOutlined />} color="orange" style={{ padding: '4px 12px', fontSize: '14px' }}>
              Coming Soon Features
            </Tag>
          </Divider>
          <Text type="secondary" style={{ display: 'block', textAlign: 'center', marginBottom: 24 }}>
            These reports are currently in development and will be available in future updates
          </Text>
        </>
      )}

      {/* Coming Soon Reports Section */}
      <div className="reports-section coming-soon-section">
        <Row gutter={[24, 24]}>
          {comingSoonReports.map((report) => (
            <Col xs={24} sm={24} md={12} lg={8} xl={8} key={report.id}>
              <Card 
                className="report-card report-card-coming-soon"
                style={{ 
                  borderTop: `4px solid ${report.color}`,
                  cursor: 'not-allowed',
                  opacity: 0.85
                }}
              >
                <div className="report-card-content">
                  <div className="report-card-icon" style={{ backgroundColor: report.bgColor, color: report.color }}>
                    {report.icon}
                  </div>
                  <div className="report-card-info">
                    <div className="report-card-header">
                      <Title level={4} className="report-card-title">{report.title}</Title>
                      <Tag color="orange" className="report-card-tag">Coming Soon</Tag>
                    </div>
                    <Text type="secondary" className="report-card-description">
                      {report.description}
                    </Text>
                    <div className="report-card-tags">
                      {report.tags.map((tag) => (
                        <Tag key={tag} color="blue" className="report-tag">
                          {tag}
                        </Tag>
                      ))}
                    </div>
                    <div className="report-card-footer">
                      <Button 
                        type="default" 
                        icon={<ClockCircleOutlined />}
                        size="middle"
                        className="report-card-button"
                        onClick={() => showError('Coming Soon', `${report.title} will be available in a future update.`)}
                      >
                        Preview Coming Soon
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      </div>

      <div className="reports-hub-footer">
        <hr className="reports-divider" />
        <Text type="secondary" className="reports-footer-text">
          Reports are generated based on your access permissions. Some reports may be limited based on your role.
        </Text>
      </div>
    </div>
  );
};

export default ReportsHub;